import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Head, Loader, EmptyBox, timeAgo } from "./shared";
import { Users, Mail, Phone, Building2, MapPin, Calendar } from "lucide-react";

const STATUSES = ["new", "contacted", "scheduled", "closed"];
const STATUS_CLS = {
  new: "bg-gold/15 text-gold border-gold/30",
  contacted: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  scheduled: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  closed: "bg-white/5 text-gray-500 border-white/10",
};

export function CohortRequests() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/cohort-requests"); setRows(data); }
    catch (e) { toast.error("Could not load cohort requests."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/cohort-requests/${id}/status`, { status });
      setRows((d) => d.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success("Status updated.");
    } catch (e) { toast.error("Update failed."); }
  };

  if (loading) return <Loader />;
  const list = rows || [];
  const newCount = list.filter((r) => (r.status || "new") === "new").length;

  return (
    <div data-testid="cohort-requests-section">
      <Head title="Cohort Requests" subtitle="Transition offices, VSOs and resource partners requesting a group launch program." />
      <div className="grid sm:grid-cols-2 gap-4 mb-8 max-w-md">
        {[["Total requests", list.length], ["New / unactioned", newCount]].map(([l, v]) => (
          <div key={l} className="border border-white/10 bg-navy-800 p-5">
            <div className="flex items-center justify-between text-gray-400 text-xs uppercase tracking-wider mb-2"><span>{l}</span><Users size={16} className="text-gold" /></div>
            <div className="text-3xl font-heading text-white">{v}</div>
          </div>
        ))}
      </div>

      {list.length === 0 ? <EmptyBox>No cohort requests yet.</EmptyBox> : (
        <div className="space-y-3">
          {list.map((r) => (
            <div key={r.id} className="border border-white/10 bg-navy-800 p-5" data-testid={`cohort-req-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-white font-semibold flex items-center gap-2"><Building2 size={15} className="text-gold" /> {r.org_name}</div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-400 mt-1.5">
                    <span>{r.contact_name}{r.role ? ` · ${r.role}` : ""}</span>
                    <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 hover:text-gold"><Mail size={13} /> {r.email}</a>
                    {r.phone && <span className="flex items-center gap-1.5"><Phone size={13} /> {r.phone}</span>}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500 mt-1">
                    {r.installation && <span className="flex items-center gap-1.5"><MapPin size={13} /> {r.installation}</span>}
                    {r.cohort_size && <span className="flex items-center gap-1.5"><Users size={13} /> {r.cohort_size}</span>}
                    {r.timeframe && <span className="flex items-center gap-1.5"><Calendar size={13} /> {r.timeframe}</span>}
                  </div>
                  {r.message && <p className="text-gray-300 text-sm mt-2 bg-white/5 border border-white/10 px-3 py-2">{r.message}</p>}
                  <div className="text-xs text-gray-600 mt-2">{timeAgo(r.created_at)}</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {STATUSES.map((s) => (
                    <button key={s} onClick={() => setStatus(r.id, s)} data-testid={`cohort-status-${r.id}-${s}`}
                      className={`text-[11px] px-2.5 py-1 border capitalize transition-colors ${(r.status || "new") === s ? STATUS_CLS[s] : "border-white/10 text-gray-500 hover:text-gray-300"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
